import { webAppApi } from '@lib/api';
import { EOneRequest } from '@lib/api/src/request';
import type { HubConnection } from '@microsoft/signalr';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';

export interface SignalRMessage {
  title: string;
  message: string;
  timestamp: string;
  linkUrl?: string;
  linkText?: string;
  isHtml?: boolean;
  entityType?: number;
  eventEntryType?: number; // 0=Error, 1=Warning, 2=Information, 3=SuccessAudit, 4=FailureAudit
}

export interface NotificationMessage {
  id: string;
  type: 'global' | 'user' | 'admin';
  title: string;
  content: string;
  timestamp: Date;
  read: boolean;
  linkUrl?: string;
  linkText?: string;
  isHtml?: boolean;
  entityType?: number;
  eventEntryType?: number; // 0=Error, 1=Warning, 2=Information, 3=SuccessAudit, 4=FailureAudit
}

class GlobalSignalRService {
  private connection: HubConnection | null = null;
  private messages: NotificationMessage[] = [];
  private messageListeners: ((messages: NotificationMessage[]) => void)[] = [];
  private isInitialized = false;
  private initializingPromise: Promise<void> | null = null;
  private readyPromise: Promise<void> | null = null;
  private readyResolve: (() => void) | null = null;
  private tokenCache: string | null = null;
  private tokenExpiry: Date | null = null;

  private progressListeners: Map<string, (message: any, messageType: string) => void> = new Map();
  private currentProcessRunsListeners: ((message: any) => void)[] = [];

  private logTransportStatus(transport: string) {
    console.log(`signalR transport: ${transport}`);
  }

  async waitForReady(): Promise<void> {
    if (this.isInitialized) {
      return Promise.resolve();
    }

    if (!this.readyPromise) {
      this.readyPromise = new Promise<void>((resolve) => {
        this.readyResolve = resolve;
      });
    }

    return this.readyPromise;
  }

  async initialize(): Promise<void> {
    if (this.isInitialized) return Promise.resolve();

    if (this.initializingPromise) {
      return this.initializingPromise;
    }

    this.initializingPromise = this.performInitialization();

    try {
      await this.initializingPromise;
    } finally {
      this.initializingPromise = null;
    }
  }

  async getAccessToken(): Promise<string | null> {
    // If cached and not expired -> return cached
    if (this.tokenCache && this.tokenExpiry && Date.now() < this.tokenExpiry.getTime()) {
      return this.tokenCache;
    }

    const tokenResp = await webAppApi.token.getToken();
    if (!tokenResp || !tokenResp.data || !tokenResp.data.data || !tokenResp.data.data.accessToken) {
      return '';
    }
    this.tokenCache = tokenResp.data.data.accessToken;
    this.tokenExpiry = new Date(tokenResp.data.data.expiresAt || '');

    return this.tokenCache;
  }

  private async performInitialization(): Promise<void> {
    try {
      // Clean up existing connection before creating a new one
      if (this.connection) {
        try {
          // Stop the existing connection gracefully
          if (this.connection.state !== 'Disconnected') {
            await this.connection.stop();
          }
        } catch (stopError) {
          // Ignore errors when stopping old connection
          console.debug('[GlobalSignalR] Error stopping old connection (expected):', stopError);
        }
        this.connection = null;
      }

      console.log('🚀 Initializing SignalR with automatic transport selection...');
      const apiSettingsResp = await webAppApi.apiSettings.getApiSettings();
      let url = '/hubs/notification';
      if (
        apiSettingsResp &&
        apiSettingsResp.data &&
        apiSettingsResp.data.data &&
        apiSettingsResp.data.data.notificationApiRootUrl
      ) {
        url = `${apiSettingsResp.data.data.notificationApiRootUrl}${url}`;
      }

      this.connection = new HubConnectionBuilder()
        .withUrl(url, {
          withCredentials: true,
          accessTokenFactory: async () => {
            const token = await this.getAccessToken();
            return token ?? '';
          },
        })
        .withAutomaticReconnect([0, 2000, 10000, 30000])
        .configureLogging(LogLevel.Debug)
        .build();

      await this.setupConnection();
      console.log('✅ SignalR initialization completed successfully');
    } catch (error) {
      console.error('❌ Global SignalR connection failed:', error);
      // Reset connection state on failure
      this.connection = null;
      this.isInitialized = false;
      throw error;
    }
  }

  // Track which groups this client is currently in so they can be rejoined after reconnect
  private activeGroups = new Set<string>();

  private async setupConnection() {
    this.connection!.onreconnecting(() => {
      console.log('SignalR: Connection lost, reconnecting...');
    });

    this.connection!.onreconnected(async (connectionId) => {
      console.log('SignalR: Reconnected with ID:', connectionId);
      // Re-join all groups that were active before the disconnection
      for (const group of this.activeGroups) {
        try {
          await this.connection!.invoke(group);
          console.log(`SignalR: Re-joined group via ${group} after reconnect`);
        } catch (err) {
          console.error(`SignalR: Failed to re-join group via ${group} after reconnect`, err);
        }
      }
    });

    this.connection!.onclose((error) => {
      if (error) {
        // Only log non-handshake errors (handshake cancellation is expected during reconnection)
        const errorMessage = error.message || String(error);
        if (
          !errorMessage.includes('Handshake was canceled') &&
          !errorMessage.includes('handshake')
        ) {
          console.log('SignalR: Connection closed with error:', error);
        } else {
          console.debug(
            'SignalR: Connection closed (handshake canceled, expected during reconnection)',
          );
        }
      } else {
        console.log('SignalR: Connection closed');
      }
      // Reset initialization state when connection closes
      this.isInitialized = false;
    });

    this.connection!.on('receiveGlobalMessage', (message: SignalRMessage) => {
      //console.log(`[SignalR] receiveGlobalMessage @ ${new Date().toISOString()}`, message);
      this.addMessage({
        id: crypto.randomUUID(),
        type: 'global',
        title: message.title || 'Global Notification',
        content: message.message || 'No content',
        timestamp: message.timestamp ? new Date(message.timestamp) : new Date(),
        read: false,
        linkUrl: message.linkUrl,
        linkText: message.linkText,
        isHtml: message.isHtml,
        entityType: message.entityType,
        eventEntryType: message.eventEntryType,
      });
    });

    this.connection!.on('receiveUserMessage', (message: SignalRMessage) => {
      //console.log(`[SignalR] receiveUserMessage @ ${new Date().toISOString()}`, message);
      this.addMessage({
        id: crypto.randomUUID(),
        type: 'user',
        title: message.title || 'Personal Notification',
        content: message.message || 'No content',
        timestamp: message.timestamp ? new Date(message.timestamp) : new Date(),
        read: false,
        linkUrl: message.linkUrl,
        linkText: message.linkText,
        isHtml: message.isHtml,
        entityType: message.entityType,
        eventEntryType: message.eventEntryType,
      });
    });

    this.connection!.on('receiveAdminMessage', (message: SignalRMessage) => {
      //console.log(`[SignalR] receiveAdminMessage @ ${new Date().toISOString()}`, message);
      this.addMessage({
        id: crypto.randomUUID(),
        type: 'admin',
        title: message.title || 'Admin Notification',
        content: message.message || 'No content',
        timestamp: message.timestamp ? new Date(message.timestamp) : new Date(),
        read: false,
        linkUrl: message.linkUrl,
        linkText: message.linkText,
        isHtml: message.isHtml,
        entityType: message.entityType,
        eventEntryType: message.eventEntryType,
      });
    });

    // Current Process Runs Messages
    this.connection!.on('receiveCurrentProgressRunsMessage', (message: SignalRMessage) => {
      //console.log(`[SignalR] receiveCurrentProgressRunsMessage @ ${new Date().toISOString()}`, message);

      // Notify listeners
      this.currentProcessRunsListeners.forEach((listener) => {
        try {
          listener(message);
        } catch (error) {
          console.error('[SignalR] Error in currentProcessRuns listener:', error);
        }
      });
    });

    // Job Progress Messages
    this.connection!.on('receiveJobProgressRunsMessage', (message: SignalRMessage) => {
      console.log(`[SignalR] receiveJobProgressRunsMessage @ ${new Date().toISOString()}`, message);
      this.progressListeners.forEach((listener) => listener(message, 'summary'));
    });

    // Progress Detail Messages
    this.connection!.on('receiveProgressDetailMessage', (message: SignalRMessage) => {
      //console.log(`[SignalR] receiveProgressDetailMessage @ ${new Date().toISOString()}`, message);
      this.progressListeners.forEach((listener) => listener(message, 'detail'));
    });

    await this.connection!.start();
    console.log('Global SignalR Connected');

    const connection = this.connection! as any;
    const transport =
      connection.transport?.constructor?.name ||
      connection._httpConnection?._transport?.constructor?.name ||
      connection.connection?.transport?.constructor?.name;

    this.logTransportStatus(transport || 'Unknown');

    this.isInitialized = true;

    if (this.readyResolve) {
      this.readyResolve();
      this.readyResolve = null;
    }
  }

  private isDuplicate(msg: NotificationMessage): boolean {
    const TWO_SECONDS = 2000;
    return this.messages.some(
      (existing) =>
        existing.title === msg.title &&
        existing.content === msg.content &&
        Math.abs(existing.timestamp.getTime() - msg.timestamp.getTime()) < TWO_SECONDS,
    );
  }

  private addMessage(message: NotificationMessage) {
    if (this.isDuplicate(message)) return;

    this.messages.unshift(message);
    if (this.messages.length > 50) {
      this.messages = this.messages.slice(0, 50);
    }

    this.notifyListeners();
  }

  async loadHistory(): Promise<void> {
    try {
      const response = await EOneRequest.get<any>('/api/notification/history', { top: 50 });
      const data = response?.data?.data;
      if (!data || !Array.isArray(data)) return;

      const historyMessages: NotificationMessage[] = data.map((item: any) => ({
        id: crypto.randomUUID(),
        type: 'user' as const,
        title: item.title || 'System Alert',
        content: item.message || '',
        timestamp: new Date(item.timestamp),
        read: true,
        linkUrl: item.linkUrl,
        linkText: item.linkText,
        isHtml: item.isHtml,
        entityType: item.entityType,
        eventEntryType: item.eventEntryType,
      }));

      this.messages = [...this.messages, ...historyMessages].slice(0, 50);
      this.notifyListeners();
    } catch (error) {
      console.error('Failed to load notification history:', error);
    }
  }

  subscribeToProgress(jobId: string, callback: (message: any, messageType: string) => void) {
    if (!this.connection || this.connection.state !== 'Connected') {
      return () => {};
    }

    this.progressListeners.set(jobId, callback);

    // Join job group with error handling
    if (this.connection && this.connection.state === 'Connected') {
      this.connection.invoke('JoinJobGroup', jobId).catch((error: any) => {
        const errorMessage = error?.message || String(error);
        // Don't log 404 errors for old connections (expected during reconnection)
        if (!errorMessage.includes('404') && !errorMessage.includes('No Connection with that ID')) {
          console.error(`[GlobalSignalR] Failed to join job group ${jobId}:`, error);
        }
      });
    }

    return () => {
      this.unsubscribeFromProgress(jobId);
    };
  }

  private unsubscribeFromProgress(jobId: string) {
    if (this.progressListeners.has(jobId)) {
      this.progressListeners.delete(jobId);
      // Leave job group with error handling
      if (this.connection && this.connection.state === 'Connected') {
        this.connection.invoke('LeaveJobGroup', jobId).catch((error: any) => {
          const errorMessage = error?.message || String(error);
          // Don't log 404 errors for old connections (expected during reconnection)
          if (
            !errorMessage.includes('404') &&
            !errorMessage.includes('No Connection with that ID')
          ) {
            console.error(`[GlobalSignalR] Failed to leave job group ${jobId}:`, error);
          }
        });
      }
    }
  }

  getActiveProgressSubscriptions(): string[] {
    return Array.from(this.progressListeners.keys());
  }

  // Current Process Runs Listeners Management
  subscribeToCurrentProcessRuns(callback: (message: any) => void): () => void {
    this.currentProcessRunsListeners.push(callback);
    return () => {
      const index = this.currentProcessRunsListeners.indexOf(callback);
      if (index > -1) {
        this.currentProcessRunsListeners.splice(index, 1);
      }
    };
  }

  // Current Job Listing Group Management
  async joinCurrentJobListingGroup(): Promise<void> {
    this.activeGroups.add('JoinCurrentJobListingGroup');
    if (this.connection && this.connection.state === 'Connected') {
      try {
        console.log('[GlobalSignalR] Joining current job listing group');
        const result = await this.connection.invoke('JoinCurrentJobListingGroup');
        console.log(
          '[GlobalSignalR] Successfully joined current job listing group, result:',
          result,
        );
      } catch (error) {
        console.error('[GlobalSignalR] Failed to join current job listing group:', error);
      }
    } else {
      console.warn('[GlobalSignalR] Cannot join current job listing group: Connection not ready');
    }
  }

  async leaveCurrentJobListingGroup(): Promise<void> {
    this.activeGroups.delete('JoinCurrentJobListingGroup');
    if (this.connection && this.connection.state === 'Connected') {
      try {
        console.log('[GlobalSignalR] Leaving current job listing group');
        await this.connection.invoke('LeaveCurrentJobListingGroup');
        console.log('[GlobalSignalR] Successfully left current job listing group');
      } catch (error) {
        console.error('[GlobalSignalR] Failed to leave current job listing group:', error);
      }
    } else {
      console.debug(
        '[GlobalSignalR] Cannot leave current job listing group: Connection not ready (this is normal during cleanup)',
      );
    }
  }

  // Job Group Management for Detail Modal
  async joinJobGroup(jobId: string): Promise<void> {
    if (this.connection && this.connection.state === 'Connected') {
      try {
        console.log(`[GlobalSignalR] Joining job group: ${jobId}`);
        await this.connection.invoke('JoinJobGroup', jobId);
        console.log(`[GlobalSignalR] Successfully joined job group: ${jobId}`);
      } catch (error) {
        console.error(`[GlobalSignalR] Failed to join job group ${jobId}:`, error);
      }
    } else {
      console.warn(`[GlobalSignalR] Cannot join job group ${jobId}: Connection not ready`);
    }
  }

  async leaveJobGroup(jobId: string): Promise<void> {
    if (this.connection && this.connection.state === 'Connected') {
      try {
        console.log(`[GlobalSignalR] Leaving job group: ${jobId}`);
        await this.connection.invoke('LeaveJobGroup', jobId);
        console.log(`[GlobalSignalR] Successfully left job group: ${jobId}`);
      } catch (error) {
        console.error(`[GlobalSignalR] Failed to leave job group ${jobId}:`, error);
      }
    } else {
      console.warn(`[GlobalSignalR] Cannot leave job group ${jobId}: Connection not ready`);
    }
  }

  getMessages(): NotificationMessage[] {
    return this.messages;
  }

  onMessagesChange(callback: (messages: NotificationMessage[]) => void) {
    this.messageListeners.push(callback);

    return () => {
      const index = this.messageListeners.indexOf(callback);
      if (index > -1) {
        this.messageListeners.splice(index, 1);
      }
    };
  }

  private notifyListeners() {
    this.messageListeners.forEach((listener) => listener(this.messages));
  }

  markAsRead(messageId: string) {
    const message = this.messages.find((m) => m.id === messageId);
    if (message) {
      message.read = true;
      this.notifyListeners();
    }
  }

  markAllAsRead() {
    this.messages.forEach((m) => (m.read = true));
    this.notifyListeners();
  }

  getUnreadCount(): number {
    return this.messages.filter((m) => !m.read).length;
  }

  get isConnected(): boolean {
    return this.connection?.state === 'Connected' || false;
  }
}

export const globalSignalR = new GlobalSignalRService();
